import React from 'react';
import { Calendar, Bookmark, Star } from 'lucide-react';
import { calculatePartisanScore } from '../scripts/bills/partisanScoring';
import { getPartisanDataFromScore } from '../scripts/bills/partisanScoreUtils';
import { getPolicyTagIcon } from '../scripts/bills/policyTagUtils';
import { getBillCardData, getBillCardBadge, getStatusColor } from '../scripts/bills/billUtils';
import InfoTooltip from './InfoTooltip';

// Memoized Bill Card Component
const BillCard = React.memo(({
  bill,
  isDarkMode,
  onBillClick,
  formatDate,

  getPolicyTagColor,
  getDaysActive,
  isSaved = false,
  onToggleSave,
  isFavorited = false,
  onToggleFavorite
}) => {
  // Use stored partisan score from database if available, otherwise calculate it
  const partisanData = (bill.partisan_score !== undefined && bill.partisan_score !== null)
    ? getPartisanDataFromScore(bill.partisan_score)
    : (calculatePartisanScore(bill) || {
      signedScore: 0,
      score: 0,
      label: 'No Data',
      direction: 'Neutral'
    });
  const mainSponsor = bill.sponsors && bill.sponsors.length > 0 ? bill.sponsors[0] : null;

  // New Status Data
  const cardData = getBillCardData(bill);
  const cardBadge = getBillCardBadge(bill, cardData.status);

  // Helper to get latest action date excluding "Effective Date"
  const getLatestActionDateExcludingEffective = (bill) => {
    if (!bill.history || bill.history.length === 0) {
      return cardData.date || bill.mostRecentDate || bill.status_date || null;
    }
    // Find first action that is NOT "Effective Date"
    const nonEffectiveAction = bill.history.find(action => {
      const actionLower = (action.action || '').toLowerCase();
      const code = action.code || '';
      return code !== 'EFF' && !actionLower.includes('effective date');
    });
    return nonEffectiveAction?.date || cardData.date || bill.mostRecentDate || bill.status_date || null;
  };
  
  const latestActionDate = getLatestActionDateExcludingEffective(bill);


  // Color logic for partisan label
  const getPartisanLabelColor = () => {
    if (!partisanData || !partisanData.label) return 'bg-gray-100 text-gray-700 border-gray-200';
    const label = partisanData.label.toLowerCase();
    if (label.includes('bipartisan') || label.includes('neutral')) {
      return 'bg-yellow-100 text-yellow-800 border-yellow-200';
    }
    if (label.includes('democratic')) {
      if (label.includes('highly')) return 'bg-blue-700 text-white border-blue-800';
      if (label.includes('strongly')) return 'bg-blue-500 text-white border-blue-600';
      if (label.includes('moderately')) return 'bg-blue-200 text-blue-800 border-blue-300';
      if (label.includes('leaning')) return 'bg-blue-100 text-blue-700 border-blue-200';
    }
    if (label.includes('republican')) {
      if (label.includes('highly')) return 'bg-red-700 text-white border-red-800';
      if (label.includes('strongly')) return 'bg-red-500 text-white border-red-600';
      if (label.includes('moderately')) return 'bg-red-200 text-red-800 border-red-300';
      if (label.includes('leaning')) return 'bg-red-100 text-red-700 border-red-200';
    }
    // fallback
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };

  // Calculate marker position for the partisan bar
  const partisanBarPercent = partisanData && typeof partisanData.signedScore === 'number'
    ? ((partisanData.signedScore + 100) / 200) * 100
    : 50;

  return (
    <div
      onClick={() => onBillClick(bill)}
      className={`group relative bg-transparent rounded-xl hover:shadow-lg transition-all duration-300 cursor-pointer border overflow-hidden ${isDarkMode ? 'border-gray-700 hover:border-blue-600' : 'border-gray-200 hover:border-blue-200'}`}
      style={{ padding: '1.5rem 2rem', marginBottom: '1.5rem' }}
    >
      {/* Favorite/Unfavorite button (star) */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleFavorite && onToggleFavorite(bill); }}
        className={`absolute top-3 right-14 p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
          } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}
        aria-label={isFavorited ? 'Unfavorite bill' : 'Favorite bill'}
        title={isFavorited ? 'Unfavorite bill' : 'Favorite bill'}
      >
        {isFavorited ? (
          <Star className={`w-5 h-5 ${isDarkMode ? 'text-yellow-300' : 'text-yellow-500'}`} fill={isDarkMode ? '#fde68a' : '#f59e0b'} />
        ) : (
          <Star className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
        )}
      </button>
      {/* Save/Unsave button */}
      <button
        onClick={(e) => { e.stopPropagation(); onToggleSave && onToggleSave(bill); }}
        className={`absolute top-3 right-3 p-2 rounded-lg transition-colors ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'
          } border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} shadow-sm`}
        aria-label={isSaved ? 'Unsave bill' : 'Save bill'}
        title={isSaved ? 'Unsave bill' : 'Save bill'}
      >
        {isSaved ? (
          <svg
            viewBox="0 0 24 24"
            className={`w-5 h-5 ${isDarkMode ? 'text-blue-400' : 'text-blue-600'}`}
            fill="currentColor"
            aria-hidden="true"
          >
            <path d="M6 4a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v16l-6-3.5L6 20V4z" />
          </svg>
        ) : (
          <Bookmark className={`w-5 h-5 ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`} />
        )}
      </button>
      {/* Top Row: Bill number, status, date */}
      <div className="flex items-start justify-between gap-2 sm:gap-4 mb-2">
        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0 flex-wrap">
          <h3 className={`text-base sm:text-lg font-bold flex-shrink-0 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{bill.bill_number}</h3>

          {/* Primary Status Label (Colored Pill) */}
          <span className={`inline-flex items-center px-2 sm:px-2.5 py-0.5 rounded-full text-[10px] sm:text-xs font-bold border border-transparent flex-shrink-0 ${getStatusColor(cardData.status, isDarkMode)}`}>
            {(cardData.label || '').toUpperCase()}
          </span>

          {/* Secondary Badge (Subtle Pill) */}
          {cardBadge && (
            <span className={`inline-flex items-center px-1.5 sm:px-2 py-0.5 rounded-full text-[9px] sm:text-[10px] font-medium border flex-shrink-0 ${cardBadge.color === 'red' ? (isDarkMode ? 'bg-red-900/30 text-red-300 border-red-800' : 'bg-red-50 text-red-700 border-red-100') :
              cardBadge.color === 'amber' ? (isDarkMode ? 'bg-amber-900/30 text-amber-300 border-amber-800' : 'bg-amber-50 text-amber-700 border-amber-100') :
                (isDarkMode ? 'bg-gray-800 text-gray-400 border-gray-700' : 'bg-gray-100 text-gray-600 border-gray-200') // Inactive/Gray
              }`}>
              {cardBadge.label}
            </span>
          )}

          {/* Date - ensure it doesn't get cut off */}
          <div className={`flex items-center text-[10px] sm:text-xs flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'}`} style={{ minWidth: 'max-content' }}>
            <Calendar className="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1 flex-shrink-0" />
            <span className="whitespace-nowrap overflow-visible">{formatDate(latestActionDate)}</span>
          </div>
        </div>
        <span className={`text-xs ml-2 sm:ml-3 self-center flex-shrink-0 ${isDarkMode ? 'text-gray-400' : 'text-gray-400'} hidden sm:inline`}>{bill.session_name}</span>
      </div>
      {/* Mobile-only session text under date */}
      <div className={`sm:hidden text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'} mb-2`}>
        {bill.session_name}
      </div>
      {/* Title */}
      <div className={`text-[1.25rem] font-bold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{bill.display_title || bill.title}</div>

      {/* Summary/Description */}
      <div className={`text-base mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{bill.ai_summary || bill.description || 'No summary available.'}</div>
      {/* Bottom Row: Policy Tags + Gauge (left), Sponsors (right, bottom) */}
      <div className="flex flex-row mt-3 items-end">
        {/* Policy Tags and Partisan Gauge - left, take up available space */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 flex-1">
          {(() => {
            const tags = bill.policy_tags || [];
            if (tags && tags.length > 0) {
              return (
                <div className="flex flex-wrap gap-2">
                  {tags.map((tag, idx) => {
                    const IconComponent = getPolicyTagIcon(tag);
                    const tagColor = getPolicyTagColor(tag);
                    return (
                      <span key={idx} className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-semibold shadow-sm border border-transparent ${tagColor}`}>
                        <IconComponent className="w-4 h-4 mr-1.5" />
                        {tag}
                      </span>
                    );
                  })}
                </div>
              );
            }
            return null;
          })()}
        </div>
        {/* Partisan Gauge - moved to bottom right */}
        <div className="ml-auto self-end">
          <InfoTooltip
            content="Calculation based on the bill's sponsor(s) party affiliation and how partisan the voting was"
            isDarkMode={isDarkMode}
            size="sm"
          >
            <div className={`inline-flex flex-col items-center px-3 py-1 transition-colors duration-150 ${isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}`} style={{ minWidth: '90px' }}>
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%', gap: 1 }}>
                {/* Arc gauge - horseshoe shape that fills from top center */}
                <div style={{ position: 'relative', width: 45, height: 45 }}>
                  {/* Background arc - complete horseshoe shape */}
                  <div style={{
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    borderRadius: '50%',
                    background: '#e5e7eb',
                    mask: `
                      radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                      conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                    `,
                    WebkitMask: `
                      radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                      conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                    `,
                    maskComposite: 'intersect',
                    WebkitMaskComposite: 'intersect',
                    zIndex: 0
                  }} />

                  {/* Democratic progress arc (counterclockwise from top center) */}
                  {partisanData && typeof partisanData.signedScore === 'number' && partisanData.signedScore < 0 && (() => {
                    const absScore = Math.abs(partisanData.signedScore);
                    const fillPercentage = Math.min(absScore, 100); // 0-100%
                    const gradientStop = Math.min(absScore, 100);

                    // Calculate the color at the gradient stop
                    const lightBlue = { r: 147, g: 197, b: 253 }; // #93c5fd
                    const darkBlue = { r: 30, g: 64, b: 175 }; // #1e40af
                    const ratio = gradientStop / 100;
                    const endColor = {
                      r: Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * ratio),
                      g: Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * ratio),
                      b: Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * ratio)
                    };
                    const endColorHex = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;

                    // Arc spans 135 degrees (from top center 270deg counterclockwise to 135deg)
                    // 100% = 135 degrees of rotation
                    const arcDegrees = fillPercentage * 1.35; // 135deg / 100 = 1.35deg per percent

                    return (
                      <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: `conic-gradient(from 0deg at 50% 50%, #93c5fd 0deg, ${endColorHex} ${arcDegrees}deg, transparent ${arcDegrees}deg)`,
                        mask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                        WebkitMask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                        maskComposite: 'intersect',
                        WebkitMaskComposite: 'intersect',
                        zIndex: 0,
                        transition: 'background 0.5s cubic-bezier(0.4,0,0.2,1)',
                        transform: 'scaleX(-1)' // Flip horizontally to make it go counterclockwise
                      }} />
                    );
                  })()}

                  {/* Republican progress arc (clockwise from top center) */}
                  {partisanData && typeof partisanData.signedScore === 'number' && partisanData.signedScore > 0 && (() => {
                    const absScore = Math.abs(partisanData.signedScore);
                    const fillPercentage = Math.min(absScore, 100); // 0-100%
                    const gradientStop = Math.min(absScore, 100);

                    // Calculate the color at the gradient stop
                    const lightRed = { r: 252, g: 165, b: 165 }; // #fca5a5
                    const darkRed = { r: 185, g: 28, b: 28 }; // #b91c1c
                    const ratio = gradientStop / 100;
                    const endColor = {
                      r: Math.round(lightRed.r + (darkRed.r - lightRed.r) * ratio),
                      g: Math.round(lightRed.g + (darkRed.g - lightRed.g) * ratio),
                      b: Math.round(lightRed.b + (darkRed.b - lightRed.b) * ratio)
                    };
                    const endColorHex = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;

                    // Arc spans 135 degrees (from top center 270deg clockwise to 45deg)
                    // 100% = 135 degrees of rotation
                    const arcDegrees = fillPercentage * 1.35; // 135deg / 100 = 1.35deg per percent

                    return (
                      <div style={{
                        position: 'absolute',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: `conic-gradient(from 0deg at 50% 50%, #fca5a5 0deg, ${endColorHex} ${arcDegrees}deg, transparent ${arcDegrees}deg)`,
                        mask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                        WebkitMask: `
                        radial-gradient(circle, transparent 40%, black 40%, black 60%, transparent 60%),
                        conic-gradient(from 225deg, black 0deg, black 270deg, transparent 270deg, transparent 360deg, black 360deg)
                      `,
                        maskComposite: 'intersect',
                        WebkitMaskComposite: 'intersect',
                        zIndex: 0,
                        transition: 'background 0.5s cubic-bezier(0.4,0,0.2,1)'
                      }} />
                    );
                  })()}

                  {/* Numerical score in the center of the arc */}
                  {partisanData && typeof partisanData.signedScore === 'number' && (() => {
                    let fillColor = '#64748b'; // Default neutral color

                    if (partisanData.signedScore < 0) {
                      // Calculate Democratic color based on score intensity
                      const absScore = Math.abs(partisanData.signedScore);
                      const gradientStop = Math.min(absScore, 100);
                      const lightBlue = { r: 147, g: 197, b: 253 }; // #93c5fd
                      const darkBlue = { r: 30, g: 64, b: 175 }; // #1e40af
                      const ratio = gradientStop / 100;
                      const endColor = {
                        r: Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * ratio),
                        g: Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * ratio),
                        b: Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * ratio)
                      };
                      fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                    } else if (partisanData.signedScore > 0) {
                      // Calculate Republican color based on score intensity
                      const absScore = Math.abs(partisanData.signedScore);
                      const gradientStop = Math.min(absScore, 100);
                      const lightRed = { r: 252, g: 165, b: 165 }; // #fca5a5
                      const darkRed = { r: 185, g: 28, b: 28 }; // #b91c1c
                      const ratio = gradientStop / 100;
                      const endColor = {
                        r: Math.round(lightRed.r + (darkRed.r - lightRed.r) * ratio),
                        g: Math.round(lightRed.g + (darkRed.g - lightRed.g) * ratio),
                        b: Math.round(lightRed.b + (darkRed.b - lightRed.b) * ratio)
                      };
                      fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                    }

                    const displayScore = Math.abs(partisanData.signedScore || 0);
                    const scoreText = displayScore === 0 ? '0' : displayScore.toFixed(1);

                    return (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        fontWeight: 700,
                        fontSize: 8,
                        color: fillColor,
                        textAlign: 'center',
                        lineHeight: 1,
                        zIndex: 4
                      }}>
                        {scoreText}
                      </div>
                    );
                  })()}
                </div>

                {/* Partisan label at the bottom, single line and centered */}
                {partisanData && partisanData.label && typeof partisanData.signedScore === 'number' && (() => {
                  let fillColor = '#64748b'; // Default neutral color

                  if (partisanData.signedScore < 0) {
                    // Calculate Democratic color based on score intensity
                    const absScore = Math.abs(partisanData.signedScore);
                    const gradientStop = Math.min(absScore, 100);
                    const lightBlue = { r: 147, g: 197, b: 253 }; // #93c5fd
                    const darkBlue = { r: 30, g: 64, b: 175 }; // #1e40af
                    const ratio = gradientStop / 100;
                    const endColor = {
                      r: Math.round(lightBlue.r + (darkBlue.r - lightBlue.r) * ratio),
                      g: Math.round(lightBlue.g + (darkBlue.g - lightBlue.g) * ratio),
                      b: Math.round(lightBlue.b + (darkBlue.b - lightBlue.b) * ratio)
                    };
                    fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                  } else if (partisanData.signedScore > 0) {
                    // Calculate Republican color based on score intensity
                    const absScore = Math.abs(partisanData.signedScore);
                    const gradientStop = Math.min(absScore, 100);
                    const lightRed = { r: 252, g: 165, b: 165 }; // #fca5a5
                    const darkRed = { r: 185, g: 28, b: 28 }; // #b91c1c
                    const ratio = gradientStop / 100;
                    const endColor = {
                      r: Math.round(lightRed.r + (darkRed.r - lightRed.r) * ratio),
                      g: Math.round(lightRed.g + (darkRed.g - lightRed.g) * ratio),
                      b: Math.round(lightRed.b + (darkRed.b - lightRed.b) * ratio)
                    };
                    fillColor = `rgb(${endColor.r}, ${endColor.g}, ${endColor.b})`;
                  }

                  return (
                    <div style={{
                      fontSize: 9,
                      fontWeight: 600,
                      color: fillColor,
                      marginTop: 0,
                      textAlign: 'center',
                      lineHeight: 1,
                      whiteSpace: 'nowrap',
                      overflow: 'visible',
                      width: '100%'
                    }}>
                      {partisanData.label}
                    </div>
                  );
                })()}
              </div>
            </div>
          </InfoTooltip>
        </div>
      </div>
    </div>
  );
});

export default BillCard; 
